import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import { prefillForCreateForm } from '../create-form-prefill';

/**
 * Paths never copied off a source entry, whatever the field is configured with. These identify the
 * source row rather than describe it: carrying them over would make the new entry claim to be the
 * old one. `archived` is re-seeded by the reshape, so it is dropped here rather than copied.
 */
const ALWAYS_EXCLUDED = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'archived'];

/**
 * The values an entry-loader field writes into the form it lives in, taken from the entry the user
 * picked.
 *
 * Deliberately a whole-entry copy minus an exclusion list, not a per-field mapping: an order
 * carries its route as a nested array of stops, each with its own activities and volumes, and
 * expressing that as a list of source/target pairs means dozens of rows that fall apart the moment
 * a stop gains a field. Everything comes over; the configuration says what must NOT.
 *
 * The reshape is shared with the create-page prefill (`prefillForCreateForm`), so a loaded entry
 * lands on the same paths a prefilled one does — including the per-form renames in
 * `sourceFormSettings.prefillMap`. A form that already names the planning's window as its departure
 * time therefore needs no second piece of configuration to also accept a loaded order.
 *
 * `excludePaths` are dotted paths in the SOURCE entry's shape (`general.status`), matching the
 * field's own configuration, and they prune the subtree: excluding `general` drops everything under
 * it.
 */
export function entryLoadValues(
  source: Entry | null | undefined,
  formModule: Module | undefined,
  excludePaths: ExcludePathConfig = [],
  sourceModule?: Module
): Entry['entry'] | undefined {
  if (!source?.entry) {
    return undefined;
  }

  const pruned = structuredCloneSafe(source.entry) as Record<string, unknown>;
  for (const path of [...ALWAYS_EXCLUDED, ...normalisePaths(excludePaths)]) {
    deletePath(pruned, path);
  }

  // Reuse the create-form reshape so a loaded entry and a prefilled one land identically. The id is
  // dropped above, so this is a value-carrier rather than a reference to the source row.
  return prefillForCreateForm({ entry: pruned } as Entry, formModule, sourceModule)?.entry;
}

/**
 * What the form builder stores for `excludePaths`. A `repeat` block yields one object per row, so
 * the rows arrive as `{ path }`; a hand-written config (or a test) may just as well use plain
 * strings. Both are accepted so neither side has to know how the other authored it.
 */
export type ExcludePathConfig = readonly (string | { path?: string } | null | undefined)[];

function normalisePaths(raw: ExcludePathConfig): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => (typeof row === 'string' ? row : row?.path))
    .map(path => path?.trim())
    .filter((path): path is string => !!path);
}

/** Remove `a.b.c` from a nested object, leaving siblings alone. A missing path is a no-op. */
function deletePath(target: Record<string, unknown>, path: string): void {
  const keys = path.split('.');
  let cursor: Record<string, unknown> = target;
  for (const key of keys.slice(0, -1)) {
    const next = cursor[key];
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      return;
    }
    cursor = next as Record<string, unknown>;
  }
  delete cursor[keys[keys.length - 1]];
}

/**
 * `structuredClone` refuses anything non-cloneable, and an entry can carry a Date or a value that
 * arrived from a template. The JSON round-trip is lossy for Dates (they become ISO strings), which
 * is what the form controls hold anyway — the same shape the API returns.
 */
function structuredCloneSafe(value: unknown): unknown {
  return JSON.parse(JSON.stringify(value));
}
